import { Card, Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getSKUStockMovement,
  getInventorySKU,
  getLocationDetails,
} from "services/data.inventory.service";
import { LoadingDialog } from "ui-components/Alert";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import { FormTitle, ReadonlyText } from "ui-components/Form";
import { ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import BasicTabs from "ui-components/tabs/BasicTabs";
import { locationDetailsColumns, stockMovementColumns } from "./inventory-mgmt/columns";
import { Error_ } from "ui-components/Alert";
import { CSVLink } from "react-csv";
import { useTeamValue } from "providers/TeamContext";
import SerialNumberLog from "./components/SerialNumberLog";
import { ExportDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";

const tabs = ["Location Details", "Stock Movement"];

function InventoryList2() {
  const { permissions, allowAll } = useTeamValue();
  const [status, setStatus] = useState("Location Details");
  const location = useLocation();
  const { customerCode, skuCode, zoneID } = location?.state ?? {};
  const [alert, setAlert] = useState(null);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const [logRequest, setLogRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const logProp = { open, setOpen, logRequest, setLogRequest };

  const exportPermission = allowAll || (Boolean(permissions["Inventory SKU List"]) && permissions["Inventory SKU List"].findIndex(b => b === "export") !== -1);

  const [skuHeader, setSKUHeader] = useState({
    id: 1,
    descriptions1: null,
    quantity: 0,
    warehouseCode: null,
    locationID: null,
  });

  const { isFetching } = useQuery({
    queryKey: ["inventory-sku"],
    queryFn: () => getInventorySKU({ customerCode, skuCode }),
    onSuccess: (data) => setSKUHeader(data.data.data),
    onError,
  });

  const render = () => {
    let task;
    switch (status) {
      case "Location Details":
        task = (
          <LocationDetails
            zoneID={zoneID}
            customerCode={customerCode}
            skuCode={skuCode}
            onError={onError}
            setSKUHeader={setSKUHeader}
            setLogRequest={setLogRequest}
            setOpen={setOpen}
          />
        );
        break;
      case "Stock Movement":
        task = (
          <StockMovement
            warehouseCode={skuHeader.warehouseCode}
            zoneID={zoneID}
            locationID={skuHeader.locationID}
            customerCode={customerCode}
            skuCode={skuCode}
            setAlert={setAlert}
            onError={onError}
            exportPermission={exportPermission}
          />
        );
        break;
      case "Outbound Details":
        task = <OutboundDetails />;
        break;
    }
    return task;
  };
  return (
    <ListLayout>
      <SerialNumberLog {...logProp} />
      {isFetching && <LoadingDialog />}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <Card sx={{ mb: 2, padding: "1rem" }}>
        <FormTitle title="SKU Information" />
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <ReadonlyText field={{ name: "skuCode", label: "SKU Code", value: skuCode }} />
          </Grid>
          <Grid item xs={6}>
            <ReadonlyText
              field={{
                name: "descriptions1",
                label: "Product Name",
                value: skuHeader.descriptions1,
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <ReadonlyText
              field={{ name: "quantity", label: "Warehouse Quantity", value: skuHeader.quantity }}
            />
          </Grid>
        </Grid>
        {render()}
      </Card>
    </ListLayout>
  );
}

export default InventoryList2;

function LocationDetails({ customerCode, skuCode, zoneID, onError, setSKUHeader, setOpen, setLogRequest }) {
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [columnFilter, setColumnFilter] = useState([{ id: "zoneID", value: zoneID }]);
  const { isFetching } = useQuery({
    queryKey: ["inventory-location-details"],
    queryFn: () => getLocationDetails({ customerCode, skuCode }),
    onSuccess: (data) => setList(data.data.data),
    onError,
  });
  const handleChange = useCallback((data) => {
    setFilteredData(data);
  }, []);

  useEffect(() => {
    const quantity = filteredData.reduce((total, detail) => {
      total = detail.quantity + total;
      return total;
    }, 0);
    setSKUHeader((prev) => ({ ...prev, quantity }));
  }, [filteredData]);

  //const [columnFilters, setColumnFilters] = useState([]);
  const tableProps = {
    data: list,
    columns: locationDetailsColumns.map(x => {
      if (x.header === "OLN") {
        x.cell = ({ row }) => {
          const { id, oln } = row.original;
          return (
            <span
              style={{ color: "#1A73E8", cursor: "pointer", textDecoration: "underline" }}
              onClick={() => {
                setOpen(true);
                setLogRequest({ id });
              }}
            >
              {oln}
            </span>
          );
        }
      }
      return x
    }),
    //columnFilters,
    //setColumnFilters,
    columnFilter,
    setColumnFilter,
    //customFilters: [{ id: "zoneID", value: zoneID }],
  };
  return (
    <>
      {isFetching && <LoadingDialog />}
      <BasicTable sx={{ mt: "2rem" }} {...tableProps} onFilterRowsChange={handleChange} />
    </>
  );
}
const date = dayjs(new Date()).format("YYYY-MM-DD");

const defaultStartDate = dayjs(new Date()).startOf("month").format("YYYY-MM-DD");

function StockMovement({
  customerCode,
  skuCode,
  warehouseCode,
  zoneID,
  locationID,
  setAlert,
  onError,
  exportPermission,
}) {
  const [list, setList] = useState([]);

  const [startDate, setStartDate] = useState(defaultStartDate);

  const [endDate, setEndDate] = useState(date);
  const [filteredData, setFilteredData] = useState([]);
  const { downloadCsv, isDownloading } = useCsvDownloadHook();

  useEffect(() => {
    setFilteredData(list)
  }, [list])
  const { isFetching, refetch } = useQuery({
    queryKey: ["inventory-stock-movement"],
    queryFn: () =>
      getSKUStockMovement({
        customerCode,
        skuCode,
        warehouseCode,
        zoneID: "",
        locationID,
        startDate,
        endDate,
      }),
    onSuccess: (data) => {
      setAlert(null);
      setList(data.data.data);
    },
    enabled: Boolean(warehouseCode) && Boolean(locationID),
    retry: 0,
    onError,
  });

  const handleChange = useCallback((data) => {
    setFilteredData(data);
  }, []);

  const tableProps = {
    data: list,
    columns: stockMovementColumns,
    onFilterRowsChange: handleChange,
  };

  console.log(filteredData);
  const customProps = { startDate, setStartDate, endDate, setEndDate };
  const customComponent2 = (
    <MDBox margin="2rem 10px 0 0">
      <Grid justifyContent="flex-end" container spacing={3}>
        <Grid item sx={8}>
          <CustomDateRangePicker {...customProps} />
        </Grid>
        <Grid item sx={2}>
          <MDButton color="info" onClick={refetch}>
            search
          </MDButton>
        </Grid>

        <Grid item sx={2}>
          {/* <MDButton color="info" disabled={!exportPermission}>
            <CSVLink
              style={{ color: "white" }}
              filename={`${skuCode}_${startDate}_${endDate}`}
              data={filteredData}
            >
              export
            </CSVLink>
          </MDButton> */}
          <MDButton
            color="info" disabled={!exportPermission}
            onClick={() => {
              const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_StockMovement_${skuCode}_${startDate}_${endDate}`;
              downloadCsv(filename, filteredData)
            }}
          >
            export
          </MDButton>
        </Grid>
      </Grid>
    </MDBox>
  );
  return (
    <>
      {(isFetching) && <LoadingDialog />}
      {isDownloading && <ExportDialog />}

      {customComponent2}

      <BasicTable {...tableProps} />
    </>
  );
}
function OutboundDetails() {
  return <>outbound details</>;
}
